import { memo } from 'react'
import {
  Check,
  type ClipboardContent,
  cn,
  Duplicate,
  Split,
  Tooltip,
  toast,
  useCopyToClipboard,
} from '@ondit/ui'
import { useNavigate, useParams } from 'react-router'
import { isLiveAssistantMessageId } from '@/lib/copilot/chat/effective-transcript'
import { useForkMothershipChat } from '@/hooks/queries/mothership-chats'
import { useChatSurface } from '@/screens/workspace/home/components/chat-surface-context'
import { useFolderStore } from '@/stores/folders/store'
import { useT } from '~/core/i18n'

const ICON_CLASS = 'size-[14px]'
const BUTTON_CLASS =
  'flex size-[26px] items-center justify-center rounded-[6px] text-[var(--text-icon)] transition-colors hover-hover:bg-[var(--surface-hover)] focus-visible:outline-hidden'

interface MessageActionsProps {
  content: string
  getCopyContent?: () => string
  hasCopyContent?: boolean
  prepareContentForCopy?: (content: string) => ClipboardContent
  /** Accepted for the callers' convenience; the actions no longer read it. */
  userQuery?: string | undefined
  requestId?: string
  messageId?: string
}

export const MessageActions = memo(function MessageActions({
  content,
  getCopyContent,
  hasCopyContent,
  prepareContentForCopy,
  messageId,
}: MessageActionsProps) {
  const t = useT()
  const navigate = useNavigate()
  const params = useParams() as { workspaceId?: string }
  const owner = params.workspaceId
  const { chatId } = useChatSurface()
  const { copied, copy: copyMessage } = useCopyToClipboard({ resetMs: 1500 })
  const forkChat = useForkMothershipChat(owner)

  const copyToClipboard = () => {
    const contentToCopy = getCopyContent?.() ?? content
    if (!contentToCopy) return
    const copyContent = prepareContentForCopy?.(contentToCopy) ?? contentToCopy
    if (typeof copyContent === 'string' && !copyContent) return
    void copyMessage(copyContent)
  }

  const handleFork = async () => {
    if (!owner || !chatId || !messageId || forkChat.isPending) return
    try {
      const result = await forkChat.mutateAsync({ chatId, upToMessageId: messageId })
      if (result.failedFileCopies) {
        toast.warning(
          t('{count} files could not be copied to the fork', { count: result.failedFileCopies })
        )
      }
      useFolderStore.getState().clearChatSelection()
      navigate(`/w/${params.workspaceId}/agent/chat/${result.id}`)
    } catch {
      toast.error(t('Failed to fork chat'))
    }
  }

  const canCopyContent = hasCopyContent ?? Boolean(content)
  // A live (just-streamed) assistant message carries a synthetic id that the
  // persisted transcript doesn't know — forking it would 400. The button
  // appears once the transcript refetch swaps in the persisted message id.
  const canFork = Boolean(owner && chatId && messageId && !isLiveAssistantMessageId(messageId))
  if (!canCopyContent && !canFork) return null

  return (
    <div className='flex items-center gap-0.5'>
      {canCopyContent && (
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <button
              type='button'
              aria-label={t('Copy message')}
              onClick={copyToClipboard}
              className={BUTTON_CLASS}
            >
              {copied ? <Check className={ICON_CLASS} /> : <Duplicate className={ICON_CLASS} />}
            </button>
          </Tooltip.Trigger>
          <Tooltip.Content side='top'>
            {copied ? t('Copied message') : t('Copy message')}
          </Tooltip.Content>
        </Tooltip.Root>
      )}
      {canFork && (
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <button
              type='button'
              aria-label={t('Fork in new chat')}
              onClick={handleFork}
              disabled={forkChat.isPending}
              className={cn(BUTTON_CLASS, forkChat.isPending && 'cursor-not-allowed opacity-50')}
            >
              <Split className={cn(ICON_CLASS, 'rotate-90')} />
            </button>
          </Tooltip.Trigger>
          <Tooltip.Content side='top'>{t('Fork in new chat')}</Tooltip.Content>
        </Tooltip.Root>
      )}
    </div>
  )
})
